import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { apiGet } from "@/lib/api";
import { getCurrentMonthRange, getMonthRange } from "@/lib/dateUtils";
import type { CouponAnalysis } from "@/types/coupon";

export function useCouponData() {
  const [userCoupon, _setUserCoupon] = useState("");
  const [couponAnalysis, setCouponAnalysis] = useState<CouponAnalysis | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>(() => {
    const current = getCurrentMonthRange();
    return {
      from: new Date(current.start + "T00:00:00Z"),
      to: new Date(current.end + "T23:59:59Z"),
    };
  });
  const [loading, setLoading] = useState(true);
  const [couponLoading, setCouponLoading] = useState(false);
  const [availableBalance, setAvailableBalance] = useState<number | null>(null);
  const { user, session, userRole } = useAuth();

  const ADMIN_COUPON_KEY = "admin_selected_coupon";

  // Wrapper that persists admin coupon selection to localStorage
  const setUserCoupon = useCallback(
    (coupon: string) => {
      _setUserCoupon(coupon);
      if (userRole === "admin" && coupon) {
        localStorage.setItem(ADMIN_COUPON_KEY, coupon);
      }
    },
    [userRole]
  );

  const setMonthRange = (year: number, month: number) => {
    const { start, end } = getMonthRange(year, month);
    setDateRange({
      from: new Date(start + "T00:00:00Z"),
      to: new Date(end + "T23:59:59Z"),
    });
  };

  // Fetch data from Supabase cache (populated by CRON)
  const fetchCouponData = useCallback(async () => {
    if (!userCoupon || !dateRange?.from || !dateRange?.to) return;

    setCouponLoading(true);
    try {
      const dateStart = dateRange.from?.toISOString().split("T")[0];
      if (!dateStart) return;

      const normalizedCoupon = userCoupon.toUpperCase();
      const res = await apiGet(
        `/api/commission-balances/fetch?coupon=${encodeURIComponent(normalizedCoupon)}&period_start=${dateStart}`
      );

      if (res.ok) {
        const data = await res.json();
        setCouponAnalysis(data);
        setLastUpdated(data.last_updated || null);
      } else if (res.status === 404) {
        // No cached data yet — CRON hasn't run for this month/coupon
        setCouponAnalysis(null);
        setLastUpdated(null);
      }
    } catch (error) {
      console.error("Error fetching coupon data:", error);
    } finally {
      setCouponLoading(false);
    }
  }, [userCoupon, dateRange]);

  // Fetch user profile to get their coupon code
  const fetchUserProfile = useCallback(
    async (retryCount = 0) => {
      if (!user || !session) {
        window.location.replace("/");
        return;
      }

      const isSessionExpired = (s: typeof session): boolean => {
        if (!s || !s.expires_at) return true;
        return Date.now() >= s.expires_at * 1000;
      };

      if (isSessionExpired(session)) {
        window.location.replace("/");
        return;
      }

      try {
        const { data: influencer, error: influencerError } = await supabase
          .from("influencers")
          .select("*")
          .eq("id", user.id)
          .single();

        if (influencerError) {
          if (retryCount < 3) {
            setTimeout(() => fetchUserProfile(retryCount + 1), 2000);
            return;
          }
          throw new Error("Erro ao buscar dados do influenciador");
        }

        if (influencer) {
          const savedCoupon = localStorage.getItem(ADMIN_COUPON_KEY);
          if (userRole === "admin" && savedCoupon) {
            _setUserCoupon(savedCoupon);
          } else {
            _setUserCoupon(influencer.coupon || "");
          }
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        toast.error("Erro ao carregar dados do dashboard");
      } finally {
        setLoading(false);
      }
    },
    [user, session]
  );

  // Fetch accumulated available balance (all-time commissions - withdrawals)
  const fetchAvailableBalance = useCallback(async () => {
    if (!user) return;
    try {
      const { data: balances, error: bErr } = await supabase
        .from("monthly_commission_balances")
        .select("commission_amount")
        .eq("user_id", user.id);
      if (bErr) throw bErr;

      const commissionsTotal = (balances || []).reduce(
        (sum, b) => sum + Number(b.commission_amount || 0), 0
      );

      const { data: withdrawals, error: wErr } = await supabase
        .from("withdrawal_requests")
        .select("amount")
        .eq("user_id", user.id)
        .in("status", ["pending", "completed"]);
      if (wErr) throw wErr;

      const withdrawnTotal = (withdrawals || []).reduce(
        (sum, w) => sum + Number(w.amount || 0), 0
      );

      setAvailableBalance(Math.max(0, commissionsTotal - withdrawnTotal));
    } catch {
      setAvailableBalance(0);
    }
  }, [user]);

  // Initial profile load
  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile, userRole]);

  // Fetch coupon data when coupon or date range changes
  useEffect(() => {
    if (!userCoupon || userCoupon.trim() === "" || !dateRange?.from || !dateRange?.to) return;
    fetchCouponData();
  }, [userCoupon, dateRange, fetchCouponData]);

  // Fetch available balance when user is ready
  useEffect(() => {
    if (user) fetchAvailableBalance();
  }, [user, fetchAvailableBalance]);

  return {
    userCoupon,
    setUserCoupon,
    couponAnalysis,
    lastUpdated,
    dateRange,
    setMonthRange,
    loading,
    couponLoading,
    availableBalance,
  };
}
